import { IProcessSettings } from './../../../shared/interfaces/process-settings';
import { Task } from 'src/app/shared/models';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap, catchError, switchMap, filter } from 'rxjs/operators';

import ITask = ZoningAPI.ITask;
import Response = ZoningAPI.Response;
import PaginatedResponse = ZoningAPI.PaginatedResponse;
import ISearchProcessesPaginatedParams = ZoningAPI.ISearchProcessesPaginatedParams;

import { environment } from '../../../../environments/environment';
import { TaskType } from '../../../shared/enums';
import { Instance, ZoningCode } from '../models';
import { InstanceService } from './instance.service';
import { BaseProcessService } from './base-process.service';
import { ITaskModel } from 'src/app/shared/interfaces/task-model.interface';
import { tasksFactory } from './task.factory';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UNLIMITED } from '../constants';
import * as _ from 'lodash';
import { IIndexationTaskParams } from '../interfaces/indexation-task-params.interface';

const API_ENDPOINT = environment.ZONAR_API_URL + '/task';
const COMMON_API_ENDPOINT = environment.ZONAR_API_URL + '/common-data';
const PROCESS_API_ENDPOINT = environment.ZONAR_API_URL + '/process';

@Injectable({
    providedIn: 'root'
})
export class TaskService extends BaseProcessService {
    totalActiveTasksCount: number = 0;
    /**
     * In case if TaskService contains processes for instance
     * then it is safe to add ned process to the list and display it
     * in Zoning Code list.
     */
    containsProcessesForInstance: boolean;
    constructor(
        protected readonly http: HttpClient,
        protected readonly snackBar: MatSnackBar,
        private readonly instanceService: InstanceService
    ) {
        super(http, snackBar);
        this.processesSubject.next([]);

        this.instanceService.currentInstance
            .pipe(
                filter(instance => Boolean(instance)),
                switchMap(instance => this.getTasksForInstance(instance))
            )
            .subscribe();
    }

    getAllTasks(page: number = 1, limit?: number) {
        const fromObject: any = {
            skipStatistic: 'false',
            getCount: 'true',
            page: String(page)
        };
        if (limit) {
            fromObject.limit = String(limit);
        }
        this.clearTasks();
        this.containsProcessesForInstance = false;
        return this.http
            .get<PaginatedResponse<ITask[]>>(`${COMMON_API_ENDPOINT}/tasks`, {
                params: new HttpParams({
                    fromObject
                })
            })
            .pipe(
                map(response => {
                    this.totalActiveTasksCount = response.rows;
                    this.clearTasks();
                    this.containsProcessesForInstance = false;
                    this.addOrUpdateProcessesWithData(response.data, false);
                })
            );
    }

    getTasksForInstance(instance: Instance) {
        const params = new HttpParams({
            fromObject: {
                instanceId: String(instance.Id),
                skipStatistic: 'true',
                page: String(UNLIMITED)
            }
        });
        this.clearTasks();
        this.containsProcessesForInstance = true;
        return this.http
            .get<Response>(API_ENDPOINT, { params })
            .pipe(
                map(response => this.getData<ITask[]>(response, [])),
                map(tasksData => {
                    this.clearTasks();
                    this.containsProcessesForInstance = true;
                    this.addOrUpdateProcessesWithData(tasksData, false);
                })
            );
    }

    getIndexZCPropertyRecordsTask({
        instance,
        zoningCodeId,
        zcGridicsId,
        offset,
        dataSourceCategories,
        saveRawIds
    }: IIndexationTaskParams) {
        const url = `${API_ENDPOINT}/index/zoning-code`;

        const fromObject = {
            zoningCodeId: String(zoningCodeId),
            zcGridicsId: String(zcGridicsId)
        };

        if (Number.isInteger(offset) && offset >= 0) {
            Object.assign(fromObject, { offset });
        }

        if (instance.State) {
            Object.assign(fromObject, { state_env: instance.State });
        }

        if (!_.isEmpty(dataSourceCategories)) {
            Object.assign(fromObject, { dataSourceCategories: JSON.stringify(dataSourceCategories) });
        }

        if (saveRawIds) {
            Object.assign(fromObject, { saveRawIds });
        }

        const params = new HttpParams({
            fromObject
        });

        return this.http
            .get<Response>(url, { params })
            .pipe(
                map(response => response.data as ITask),
                tap(taskData => {
                    const taskParams = {
                        zoningCodeId: taskData.zoningCodeId,
                        instanceId: taskData.instanceId,
                        type: taskData.type
                    };

                    this.updateTaskByParams(taskData, taskParams);
                })
            );
    }

    getIndexZCPropertyRecordsWithCursorTask({
        instance,
        zoningCodeId,
        zcGridicsId,
        cursorMark,
        dataSourceCategories,
        saveRawIds
    }: IIndexationTaskParams) {
        const url = `${API_ENDPOINT}/index-with-cursor/zoning-code`;

        const fromObject = {
            zoningCodeId: String(zoningCodeId),
            zcGridicsId: String(zcGridicsId)
        };

        if (cursorMark) {
            Object.assign(fromObject, { cursorMark });
        }

        if (instance.State) {
            Object.assign(fromObject, { state_env: instance.State });
        }

        if (!_.isEmpty(dataSourceCategories)) {
            Object.assign(fromObject, { dataSourceCategories: JSON.stringify(dataSourceCategories) });
        }

        if (saveRawIds) {
            Object.assign(fromObject, { saveRawIds });
        }

        const params = new HttpParams({
            fromObject
        });

        return this.http
            .get<Response>(url, { params })
            .pipe(
                map(response => response.data as ITask),
                tap(taskData => {
                    const taskParams = {
                        zoningCodeId: taskData.zoningCodeId,
                        instanceId: taskData.instanceId,
                        type: taskData.type
                    };

                    this.updateTaskByParams(taskData, taskParams);
                })
            );
    }

    private updateTaskByParams(taskData: ITask, taskParams: any) {
        const taskWithTheSameParams = this.getExistingTaskByParams(taskParams);

        if (taskWithTheSameParams) {
            taskWithTheSameParams.initWithJSON(taskData);
        } else {
            this.processNewProcess(taskData);
        }
    }

    private addOrUpdateProcessWithData(
        taskData: ITask,
        skipStatistic: boolean = false,
        updateOnly: boolean = false
    ): ITaskModel {
        const existingTask = this.processesSubject.value.find(task => task.id === taskData.id);

        if (!existingTask) {
            if (!updateOnly) {
                const task = tasksFactory(taskData);
                this.processesSubject.next([...this.processesSubject.value, task]);
                return task;
            }
            return null;
        } else {
            existingTask.initWithJSON(taskData, skipStatistic);
            this.processesSubject.next([].concat(this.processesSubject.value));
            return existingTask;
        }
    }

    private getExistingTaskByParams(taskData: any): ITaskModel {
        if (!taskData) {
            return;
        }
        for (const task of this.processesSubject.value) {
            let taskFounded = true;

            for (const [property, value] of Object.entries(taskData)) {
                if (!task.hasOwnProperty(property) || task[property] !== value) {
                    taskFounded = false;
                    break;
                }
            }

            if (taskFounded) {
                return task;
            }
        }

        return null;
    }

    addOrUpdateProcessesWithData(tasksData: ITask[], updateOnly: boolean) {
        this.processesSubject.next(this.getUpdatedProcessesWithData(tasksData, updateOnly));
    }

    private getUpdatedProcessesWithData(tasksData: ITask[], updateOnly: boolean) {
        const newTasks: ITaskModel[] = [];
        if (_.isEmpty(tasksData)) {
            return this.processesSubject.value;
        }
        for (const taskData of tasksData) {
            const existingTask = this.processesSubject.value.find(task => task.id === taskData.id);
            if (!existingTask) {
                if (!updateOnly) {
                    const task = tasksFactory(taskData);
                    newTasks.push(task);
                }
            } else {
                existingTask.initWithJSON(taskData);
            }
        }
        return newTasks.length ? [...this.processesSubject.value, ...newTasks] : this.processesSubject.value;
    }

    startTask(task: ITaskModel, skipStatistic: boolean = false) {
        return this.continueProcess(task, skipStatistic).pipe(
            tap(taskData => this.addOrUpdateProcessWithData(taskData, skipStatistic, true)),
            catchError(() => {
                this.removeTask(task);
                return of(task);
            })
        );
    }

    stopTask(task: ITaskModel, skipStatistic: boolean = false) {
        return this.stopProcess(task, skipStatistic).pipe(
            tap(taskData => this.addOrUpdateProcessWithData(taskData, skipStatistic, true)),
            catchError(() => {
                this.removeTask(task);
                return of(task);
            })
        );
    }

    deleteTask(task: ITaskModel) {
        return this.deleteProcess(task, true).pipe(
            tap(taskData => this.removeTask(taskData)),
            catchError(() => {
                this.removeTask(task);
                return of(task);
            })
        );
    }

    updateTask(task: ITaskModel, partialModel) {
        return this.updateProcess(task, partialModel).pipe(
            tap(taskData => this.addOrUpdateProcessWithData(taskData, undefined, true)),
            catchError(() => {
                this.removeTask(task);
                return of(task);
            })
        );
    }

    // TODO :: This is specific method. Need to review and create appropriate method in ProcessService
    checkStatusOfTaskByZC(
        zoningCode: ZoningCode,
        type: TaskType | TaskType[],
        removingPredicate?: any,
        skipStatistic?: boolean,
        stateEnv?: string
    ) {
        const fromObject: any = {
            zoningCodeId: String(zoningCode.Id),
            zcGridicsId: String(zoningCode.GridicsId),
            skipStatistic: String(!!skipStatistic),
            type: _.isArray(type) ? JSON.stringify(type) : String(type)
        };

        if (stateEnv) {
            fromObject.state_env = stateEnv;
        }

        let params = new HttpParams({
            fromObject
        });

        return this.http
            .get<Response>(`${PROCESS_API_ENDPOINT}/search`, { params })
            .pipe(
                map(response => _.defaultTo(response.data, [])[0] as ITask),
                map(task => this.initEntity(task)),
                tap(task => {
                    this.addOrUpdateProcessWithData(task);
                    return of(this.processesSubject.value.find(t => t.id === task.id));
                }),
                catchError(() => {
                    if (removingPredicate) {
                        this.removeTask(this.processesSubject.value.find(removingPredicate));
                    }
                    return of(null);
                })
            );
    }

    searchTasks(searchParams: ISearchProcessesPaginatedParams): Observable<ITaskModel[]> {
        const { skipStatistic, zoningCodeId, zcGridicsId, type, state_env, getArchived } = searchParams || {};
        if (!zoningCodeId && !zcGridicsId) {
            return of([] as ITaskModel[]);
        }
        const fromObject: any = {
            skipStatistic: String(!!skipStatistic),
            type: _.isArray(type) ? JSON.stringify(type) : String(type)
        };

        if (!_.isUndefined(zoningCodeId)) {
            fromObject.zoningCodeId = String(zoningCodeId);
        }

        if (!_.isUndefined(zcGridicsId)) {
            fromObject.zoningCodeId = String(zcGridicsId);
        }

        if (!_.isUndefined(state_env)) {
            fromObject.state_env = String(state_env);
        }

        if (!_.isUndefined(getArchived)) {
            fromObject.getArchived = String(getArchived);
        }

        let params = new HttpParams({ fromObject });

        return this.http
            .get<Response>(`${PROCESS_API_ENDPOINT}/search`, { params })
            .pipe(
                map(response => response.data as ITask[]),
                map(tasks => tasks.map(this.initEntity.bind(this)) as ITaskModel[]),
                catchError(() => {
                    return of([] as ITaskModel[]);
                })
            );
    }

    getTaskById(task: ITaskModel) {
        return this.uploadProcessById(task).pipe(
            map(taskData => this.addOrUpdateProcessWithData(taskData, undefined, true)),
            catchError(() => {
                this.removeTask(task);
                return of(null);
            })
        );
    }

    getTaskByIdAndState(
        param: { id: string; state: string },
        skipStatistic: boolean = false,
        getEventsStat: boolean = true
    ): Observable<ITaskModel> {
        const params = new HttpParams({
            fromObject: {
                state_env: param.state,
                skipStatistic: String(skipStatistic),
                getEventsStat: String(getEventsStat)
            }
        });
        return this.http
            .get<ITask>(`${PROCESS_API_ENDPOINT}/${param.id}`, { params })
            .pipe(
                catchError(err => {
                    console.log(err);
                    return of(null);
                }),
                map((response: any) => new Task(this.getData<ITask>(response, null)))
            );
    }

    checkTaskStatus(task) {
        return this.getTaskById(task).subscribe();
    }

    removeTask(task: ITask) {
        if (!task) {
            return;
        }

        if (!this.containsProcessesForInstance) {
            this.notifyProcessListChanged();
            return;
        }

        const tasks = this.processesSubject.value.filter(ownTask => ownTask.id !== task.id);
        this.processesSubject.next(tasks);
    }

    removeTasks(tasks: ITask[]) {
        if (_.isEmpty(tasks)) {
            return;
        }

        if (!this.containsProcessesForInstance) {
            this.notifyProcessListChanged();
            return;
        }

        const remainingTasks = this.processesSubject.value.filter(
            ownTask => !tasks.find(task => ownTask.id === task.id)
        );
        this.processesSubject.next(remainingTasks);
    }

    calculateZCVersion(
        instanceId: number,
        zcVersionId: number,
        zcGridicsId: string,
        processSettings?: IProcessSettings
    ) {
        const url = `${API_ENDPOINT}/calculate/zoning-code-version`;

        const params = new HttpParams({
            fromObject: {
                instanceId: String(instanceId),
                zcVersionId: String(zcVersionId),
                zcGridicsId: String(zcGridicsId),
                ...this.getProcessSettings(processSettings)
            }
        });

        return this.http
            .get<Response>(url, { params })
            .pipe(
                map(response => response.data as ITask),
                tap(taskData => {
                    const taskParams = {
                        zcVersionId: taskData.zcVersionId,
                        instanceId: taskData.instanceId,
                        type: taskData.type
                    };

                    this.updateTaskByParams(taskData, taskParams);
                })
            );
    }

    recalculateZCVersion(
        instance: Instance,
        zcVersionId: number,
        zcGridicsId: string,
        processSettings?: IProcessSettings
    ) {
        const url = `${API_ENDPOINT}/recalculate/zoning-code-version`;

        let params = new HttpParams({
            fromObject: {
                instanceId: String(instance.Id),
                zcVersionId: String(zcVersionId),
                zcGridicsId: String(zcGridicsId),
                ...this.getProcessSettings(processSettings)
            }
        });

        if (instance.State) {
            params = params.set('state_env', String(instance.State));
        }

        return this.http
            .get<Response>(url, { params })
            .pipe(
                map(response => response.data as ITask),
                tap(taskData => {
                    const taskParams = {
                        zcVersionId: taskData.zcVersionId,
                        instanceId: taskData.instanceId,
                        type: taskData.type
                    };

                    this.updateTaskByParams(taskData, taskParams);
                })
            );
    }

    getProcessSettings(processSettings: IProcessSettings) {
        if (!processSettings) {
            return {};
        }
        return {
            dataSourceCategories: JSON.stringify(_.get(processSettings, 'dataSourceCategories')),
            maxThreadsCount: String(_.get(processSettings, 'maxThreadsCount')),
            maxPRsBunchSize: String(_.get(processSettings, 'maxPRsBunchSize')),
            useGAPIDataCache: String(_.get(processSettings, 'useGAPIDataCache')),
            syncSolrData: String(_.get(processSettings, 'syncSolrData'))
        };
    }

    clearThreads(task: ITaskModel, skipStatistic: boolean = false): Observable<ITaskModel> {
        return super.clearThreads(task, skipStatistic).pipe(
            map(taskData => taskData as ITaskModel),
            tap(taskData => this.addOrUpdateProcessWithData(taskData, skipStatistic, true)),
            catchError(() => {
                this.removeTask(task);
                return of(task);
            })
        );
    }

    clearTasks() {
        this.processesSubject.next([]);
    }

    processNewProcess(processData: ITask) {
        if (!processData) {
            return;
        }
        if (this.containsProcessesForInstance) {
            this.addOrUpdateProcessWithData(processData);
        } else {
            this.notifyProcessListChanged();
        }
    }

    setIsContainsProcessesForInstance(value: boolean) {
        this.containsProcessesForInstance = value;
    }
}
